/**
 * Settings → Devices → "This phone": the home-screen widget's amounts (mobile
 * M7). The widget shows "Due by month end" to anyone who sees the phone, so
 * it can show dots instead (the owner's answer to plan Q7: amounts on, with a
 * switch). Android app only.
 */

import * as SwitchPrimitive from "@radix-ui/react-switch";
import { useQueryClient } from "@tanstack/react-query";
import { LayoutGrid } from "lucide-react";
import * as React from "react";

import type { DashboardSummary } from "@/lib/api/client";
import { setWidgetHidesAmounts, widgetHidesAmounts, widgetSummary } from "@/lib/widgetSummary";
import { isAndroidApp } from "@/platform";
import { setMonthEndWidget } from "@/platform/widget";

export function PhoneWidget() {
  const qc = useQueryClient();
  const [hide, setHide] = React.useState(widgetHidesAmounts);
  if (!isAndroidApp()) return null;

  const toggle = (next: boolean) => {
    setWidgetHidesAmounts(next);
    setHide(next);
    const data = qc.getQueryData<DashboardSummary>(["dashboard"]);
    if (data) void setMonthEndWidget(widgetSummary(data, Date.now(), next));
  };

  return (
    <div className="flex items-start gap-3 border-t border-hairline px-5 py-4">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-hairline bg-raised text-muted">
        <LayoutGrid className="h-4 w-4" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <label htmlFor="widget-hide" className="text-sm text-ink">
          Hide amounts on the home-screen widget
        </label>
        <p className="mt-0.5 text-xs text-muted">
          Long-press your home screen → Widgets → MyDeck to add it. It shows what's due by month end.
        </p>
      </div>
      <SwitchPrimitive.Root
        id="widget-hide"
        checked={hide}
        onCheckedChange={toggle}
        className="relative mt-1 h-7 w-12 shrink-0 rounded-full border border-hairline bg-raised transition-colors data-[state=checked]:border-brass/50 data-[state=checked]:bg-brass/30"
      >
        <SwitchPrimitive.Thumb className="block h-5 w-5 translate-x-0.5 rounded-full bg-ink shadow transition-transform data-[state=checked]:translate-x-[1.375rem]" />
      </SwitchPrimitive.Root>
    </div>
  );
}
